import { cx } from "@emotion/css";
import useDeviceType from "hooks/useDeviceType";
import OrderSummaryBoxLayout from "library/Layouts/OrderDetailsBoxLayout/OrderSummaryBoxLayout/OrderSummaryBoxLayout";
import { first, size } from "lodash";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContactPaymentType } from "store/features/contact.store";
import { retrieveContactTimezone } from "store/features/localization.store";
import { formatDate, formatDateFlat, formatHours, noonOfDay } from "utility/date-fns";
import { formatPrice } from "utility/helpers/general";
import { getPriceWithVat, prettifyPackagesAmount } from "utility/helpers/orders";
import { NEW_ORDER_FORM_STEPS, REPEAT_ORDER_TYPES } from "../../../../constants";
import MessageLayout from "../../../../library/Layouts/MessageLayout";
import useNewOrderMachine from "../../hooks/useNewOrderMachine";
import IncreasedPriceMessage from "./IncreasedPriceMessage/IncreasedPriceMessage";
import styles from "./NewOrderDetails.styles";

const NewOrderDetails = () => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceType();

  const timezone = useSelector(retrieveContactTimezone);
  const { isCheckoutPaymentType } = useSelector(retrieveContactPaymentType);

  const [
    {
      order: { startDate, endDate, pick_up, drops, isExpress, repeat_settings },
      price,
    },
    { step },
  ] = useNewOrderMachine();

  const { totalPrice, startingPrice } = useMemo(() => {
    return price.reduce(
      (prev, curr) => {
        const currPrice = getPriceWithVat(curr, isCheckoutPaymentType);
        return {
          totalPrice: prev.totalPrice + currPrice.totalPrice,
          startingPrice: prev.startingPrice + currPrice.startingPrice,
        };
      },
      { totalPrice: 0, startingPrice: 0 }
    );
  }, [isCheckoutPaymentType, price]);

  const newOrderDate = useMemo(() => formatDate(startDate, timezone), [startDate, timezone]);
  const newOrderPickUpTime = useMemo(() => formatHours(startDate, timezone), [startDate, timezone]);
  const newOrderDropOffTime = useMemo(() => formatHours(endDate, timezone), [endDate, timezone]);

  const isLastStep = useMemo(() => step === NEW_ORDER_FORM_STEPS.BOOKING, [step]);
  const shouldShowDiscount = useMemo(() => totalPrice !== startingPrice, [startingPrice, totalPrice]);

  return (
    <OrderSummaryBoxLayout
      className={cx(
        isMobile ? "order-first my-4" : styles.container(isLastStep),
        "col-12 col-md-6 px-0 pl-md-4 pr-md-0"
      )}
      dateSection={newOrderDate}
      pickUpSection={`${newOrderPickUpTime} - ${pick_up.location} | ${pick_up.postcode}`}
      pickUpNotesSection={pick_up.notes}
      dropsSection={drops.map(({ reference, location, postcode, packages, notes }, dindex) => (
        <OrderSummaryBoxLayout.Drops
          key={reference}
          titleSection={`${t("utils.ui.orderSummary.dropOff")} ${size(drops) > 1 ? `#${dindex + 1}` : ""}`}
          dropAddressSection={`${newOrderDropOffTime} - ${location} | ${postcode}`}
          packageTypeSection={prettifyPackagesAmount(packages)}
          notesSection={notes || null}
        />
      ))}
      priceSection={
        isLastStep && (
          <Trans
            i18nKey={isCheckoutPaymentType ? "utils.pricing.inclVAT" : "utils.pricing.exclVAT"}
            context={shouldShowDiscount ? "discount" : ""}
            tOptions={{
              price: formatPrice(totalPrice, first(price).currency),
              discount: formatPrice(startingPrice, first(price).currency),
            }}
            components={{ span: <span />, del: <del />, span_discount: <span /> }}
            defaults={
              shouldShowDiscount
                ? `<span>{{price}}</span> <del>(<span_discount>{{discount}}</span_discount>)</del> ${
                    isCheckoutPaymentType ? "inc" : "excl"
                  }. VAT`
                : `<span>{{price}}</span> ${isCheckoutPaymentType ? "inc" : "excl"}. VAT`
            }
          />
        )
      }
      repeatOrderSection={
        repeat_settings.type === REPEAT_ORDER_TYPES.EXACT_DATES &&
        repeat_settings.dates
          .map((dateToRepeat) => formatDateFlat(noonOfDay(new Date(dateToRepeat)), timezone))
          .join(", ")
      }
      messagesSection={
        <>
          <IncreasedPriceMessage startDate={startDate} isExpress={isExpress} />

          {!isCheckoutPaymentType && (
            <MessageLayout
              className="col-12 mt-3 px-0 w-100"
              labelSection={<>{t("newOrder.estimation.notFinalPriceMessage")}</>}
              data-cy="not-final-price-message"
            />
          )}
        </>
      }
      collapse={size(drops) > 4}
      data-cy="new-order-details"
    />
  );
};

export default NewOrderDetails;
